<!--Author: Michał Pawłowski-->
<template>
  <div class="common-form">
    <h5 v-if="title" class="common-form__title">{{ title }}</h5>

    <div class="form-group">
      <div v-for="row in sortedRows" :key="row.name" :class="row.rowClass">
        <div
          v-for="field in row.fields"
          :key="field.name"
          :class="field.colClass"
        >

          <!-- section -->
          <div v-if="field.type === 'section'" class="common-form__section">
            <hr class="border-light my-4" />
            <h6 class="text-uppercase text-muted fw-bold mb-3 fs-12">{{ field.label }}</h6>
            <div
              v-if="field.value !== null && field.value !== undefined && field.value.length > 0"
              class="common-form__section-description"
            >
              {{ field.value }}
            </div>
          </div>

          <!-- text field -->
          <div v-if="field.type === 'text'" class="common-form__field mb-3">
            <label
              v-if="!isEmpty(field.label)"
              :for="field.name + '-id'"
              class="common-form__label"
            >
              {{ field.label }}
              <span v-if="field.required" class="common-form__required">*</span>
              <span v-if="!isEmpty(field.helpText)" class="common-form__help-icon" :title="field.helpText">?</span>
            </label>
            <input
              :id="field.name + '-id'"
              :ref="field.name + '-id'"
              :type="field.type"
              :placeholder="field.placeholder"
              :class="['common-form__input form-control', field.inputClass === 'form-control is-invalid' ? 'is-invalid' : '']"
              v-model="field.value"
              :aria-describedby="field.name + '-input-feedback'"
              :disabled="field.disabled"
              :minlength="field.minLength"
              :maxlength="field.maxLength"
            />
            <div class="invalid-feedback" :id="field.name + '-input-feedback'">
              {{ field.inputFeedbackText }}
            </div>
          </div>

          <!-- textarea field -->
          <div v-if="field.type === 'textarea'" class="common-form__field mb-3">
            <label
              v-if="!isEmpty(field.label)"
              :for="field.name + '-id'"
              class="common-form__label"
            >
              {{ field.label }}
              <span v-if="field.required" class="common-form__required">*</span>
              <span v-if="!isEmpty(field.helpText)" class="common-form__help-icon" :title="field.helpText">?</span>
            </label>
            <textarea
              :id="field.name + '-id'"
              :ref="field.name + '-id'"
              :placeholder="field.placeholder"
              :class="['common-form__input form-control', field.inputClass === 'form-control is-invalid' ? 'is-invalid' : '']"
              v-model="field.value"
              :aria-describedby="field.name + '-input-feedback'"
              :disabled="field.disabled"
              rows="3"
            ></textarea>
            <div class="invalid-feedback" :id="field.name + '-input-feedback'">
              {{ field.inputFeedbackText }}
            </div>
          </div>

          <!-- date field -->
          <div v-if="field.type === 'date'" class="common-form__field mb-3">
            <label
              v-if="!isEmpty(field.label)"
              :for="field.name + '-id'"
              class="common-form__label"
            >
              {{ field.label }}
              <span v-if="field.required" class="common-form__required">*</span>
              <span v-if="!isEmpty(field.helpText)" class="common-form__help-icon" :title="field.helpText">?</span>
            </label>
            <input
              :id="field.name + '-id'"
              :ref="field.name + '-id'"
              type="date"
              :placeholder="field.placeholder"
              :class="['common-form__input form-control', field.inputClass === 'form-control is-invalid' ? 'is-invalid' : '']"
              v-model="field.value"
              :aria-describedby="field.name + '-input-feedback'"
              :disabled="field.disabled"
            />
            <div class="invalid-feedback" :id="field.name + '-input-feedback'">
              {{ field.inputFeedbackText }}
            </div>
          </div>

          <!-- number field -->
          <div v-if="field.type === 'number'" class="common-form__field mb-3">
            <label
              v-if="!isEmpty(field.label)"
              :for="field.name + '-id'"
              class="common-form__label"
            >
              {{ field.label }}
              <span v-if="field.required" class="common-form__required">*</span>
              <span v-if="!isEmpty(field.helpText)" class="common-form__help-icon" :title="field.helpText">?</span>
            </label>
            <input
              :id="field.name + '-id'"
              :ref="field.name + '-id'"
              type="number"
              :placeholder="field.placeholder"
              :class="['common-form__input form-control', field.inputClass === 'form-control is-invalid' ? 'is-invalid' : '']"
              v-model="field.value"
              :aria-describedby="field.name + '-input-feedback'"
              :disabled="field.disabled"
              :minValue="field.minValue"
              :maxValue="field.maxValue"
            />
            <div class="invalid-feedback" :id="field.name + '-input-feedback'">
              {{ field.inputFeedbackText }}
            </div>
          </div>

          <!-- file field -->
          <div v-if="field.type === 'file'" class="common-form__field mb-3">
            <label
              v-if="!isEmpty(field.label)"
              :for="field.name + '-id'"
              class="common-form__label"
            >
              {{ field.label }}
              <span v-if="field.required" class="common-form__required">*</span>
              <span v-if="!isEmpty(field.helpText)" class="common-form__help-icon" :title="field.helpText">?</span>
            </label>
            <input
              :id="field.name + '-id'"
              :ref="field.name + '-id'"
              type="file"
              :placeholder="field.placeholder"
              :class="['common-form__input form-control', field.inputClass === 'form-control is-invalid' ? 'is-invalid' : '']"
              @input="field.value = $event.target.files ? $event.target.files[0] : null"
              :aria-describedby="field.name + '-input-feedback'"
              :disabled="field.disabled"
            />
            <div class="invalid-feedback" :id="field.name + '-input-feedback'">
              {{ field.inputFeedbackText }}
            </div>
          </div>

          <!-- list / key-value-list field -->
          <div
            v-if="field.type === 'list' || field.type === 'key-value-list'"
            class="common-form__field mb-3"
          >
            <label
              v-if="!isEmpty(field.label)"
              class="common-form__label"
            >
              {{ field.label }}
              <span v-if="field.required" class="common-form__required">*</span>
              <span v-if="!isEmpty(field.helpText)" class="common-form__help-icon" :title="field.helpText">?</span>
            </label>
            <div
              v-if="!field.disabled"
              :id="field.name + '-id'"
              :ref="field.name + '-id'"
              :class="['common-form__list-container', field.inputClass === 'form-control is-invalid' ? 'is-invalid' : '']"
              :aria-describedby="field.name + '-input-feedback'"
            >
              <MutableScrollableListView
                v-if="field.type === 'list'"
                title=""
                :listContent="field.value"
                :elementInputPlaceholder="field.placeholder"
                @onAddElement="addItem($event, row.name, field.name)"
                @onRemoveElement="removeItem($event, row.name, field.name)"
              />
              <MutableScrollableKeyValueTypedListView
                v-else
                title=""
                :listContent="field.value"
                :elementInputPlaceholder="field.placeholder"
                @onAddElement="addItem($event, row.name, field.name)"
                @onRemoveElement="removeItem($event, row.name, field.name)"
              />
            </div>
            <div class="invalid-feedback" :id="field.name + '-input-feedback'">
              {{ field.inputFeedbackText }}
            </div>
          </div>

          <!-- radio field -->
          <div v-if="field.type === 'radio'" class="common-form__field mb-3">
            <label
              v-if="!isEmpty(field.label)"
              class="common-form__label"
            >
              {{ field.label }}
              <span v-if="field.required" class="common-form__required">*</span>
              <span v-if="!isEmpty(field.helpText)" class="common-form__help-icon" :title="field.helpText">?</span>
            </label>
            <div class="common-form__radio-group">
              <div
                v-for="(value, index) in field.options"
                :key="index"
                class="form-check"
              >
                <input
                  class="form-check-input"
                  :id="'radio-option-id-' + field.name + '-' + index"
                  :ref="'radio-option-id-' + index"
                  type="radio"
                  :name="field.name"
                  :value="value"
                  v-model="field.value"
                  :disabled="field.disabled"
                />
                <label class="form-check-label" :for="'radio-option-id-' + field.name + '-' + index">{{ value }}</label>
              </div>
            </div>
            <div :class="field.inputClass" :id="field.name + '-input-feedback'">
              {{ field.inputFeedbackText }}
            </div>
          </div>

          <!-- checkbox field -->
          <div v-if="field.type === 'checkbox'" class="common-form__field mb-3">
            <div class="form-check">
              <input
                class="form-check-input"
                :id="'checkbox-option-id-' + field.name"
                :ref="'checkbox-option-id-' + field.name"
                type="checkbox"
                :name="field.name"
                :value="field.value"
                v-model="field.value"
                :disabled="field.disabled"
              />
              <label class="form-check-label" :for="'checkbox-option-id-' + field.name">
                {{ field.label }}
                <span v-if="field.required" class="common-form__required">*</span>
                <span v-if="!isEmpty(field.helpText)" class="common-form__help-icon" :title="field.helpText">?</span>
              </label>
            </div>
            <div :class="field.inputClass" :id="field.name + '-input-feedback'">
              {{ field.inputFeedbackText }}
            </div>
          </div>

          <!-- select field -->
          <div v-if="field.type === 'select'" class="common-form__field mb-3">
            <label
              v-if="!isEmpty(field.label)"
              :for="field.name + '-id'"
              class="common-form__label"
            >
              {{ field.label }}
              <span v-if="field.required" class="common-form__required">*</span>
              <span v-if="!isEmpty(field.helpText)" class="common-form__help-icon" :title="field.helpText">?</span>
            </label>
            <select
              :id="field.name + '-id'"
              :ref="field.name + '-id'"
              :class="['common-form__input form-select', field.inputClass === 'form-control is-invalid' ? 'is-invalid' : '']"
              v-model="field.value"
              @input="field.value = $event.target.value"
              :aria-describedby="field.name + '-input-feedback'"
              :disabled="field.disabled"
            >
              <option
                v-for="(value, index) in field.options"
                :selected="field.value === value"
                :key="index"
                :value="value"
              >
                {{ value }}
              </option>
            </select>
            <div class="invalid-feedback" :id="field.name + '-input-feedback'">
              {{ field.inputFeedbackText }}
            </div>
          </div>

          <!-- button field -->
          <div v-if="field.type === 'button'" class="common-form__field mb-3">
            <div
              :id="field.name + '-id'"
              :ref="field.name + '-id'"
              :class="field.inputClass"
              style="width: 100%;"
              @click="runFieldAction(field)"
            >
              {{ field.label }}
            </div>
          </div>

          <!-- slot field -->
          <div v-if="field.type === 'slot'" class="common-form__field mb-3">
            <div
              :id="field.name + '-id'"
              :ref="field.name + '-id'"
              :class="field.inputClass"
            >
              <slot :name="field.name"></slot>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Footer actions -->
    <div v-if="submitButtonEnabled || cancelButtonEnabled" class="common-form__footer">
      <button
        v-if="cancelButtonEnabled"
        type="button"
        class="btn btn-outline-secondary common-form__footer-btn"
        @click="$emit('onCancel')"
      >
        {{ cancelButtonText }}
      </button>
      <button
        v-if="submitButtonEnabled"
        type="button"
        class="btn btn-primary common-form__footer-btn"
        @click="saveForm()"
      >
        {{ submitButtonText }}
      </button>
    </div>

  </div>
</template>

<script>
import MutableScrollableListView from "./MutableScrollableListView.vue";
import MutableScrollableKeyValueTypedListView from "./MutableScrollableKeyValueTypedListView.vue";
import {log} from "@/state/pinia/log";

export default {
  name: "CommonForm",
  props: { title: String, structure: Array, data:Object, submitButtonText: String,submitButtonEnabled: Boolean,
  cancelButtonText: String, cancelButtonEnabled: Boolean },
  emits: ["onFieldAction", "onSubmit", "onCancel"],
  components: {
    MutableScrollableListView,
    MutableScrollableKeyValueTypedListView,
  },
  data() {
    return {
      sortedRows: [],

      allFields: [],
      nameInputClass: "form-control",
      defaultInvalidInputClass: "form-control is-invalid",
      defaultValidInputClass: "form-control",
      defaultInputFeedbackText: "Uzupełnij pole",

      exampleRow: {
        order: 0,
        name: "",
        rowClass: "row",
        fields: [],
      },

      exampleField: {
        order: 0,
        name: "specific-field-name", // name is also a key in JSON
        value: null,
        type: "text", // available types: text,textarea, number, date, checkbox,radio, list, select,file, button and section
        label: "Nazwa pola",
        required: true,
        options: [], // for select type
        minLength: 3, // used only to validate text fields
        maxLength: 20, // used only to validate text fields
        minValue: 0.0, // must be float or double and is used only to type number
        maxValue: 100.0, // must be float or double and is used only to type number
        pattern: "^[a-zA-Z0-9]+$",
        placeholder: "Wpisz nazwę pola",
        helpText: "Pole musi zawierać od 3 do 20 znaków",
        colClass: "col col-lg-3",
      },
    };
  },
  computed: {
  },
  methods: {
    isEmpty(value) {
      return value === null || value === undefined || value.length === 0;
    },
    runFieldAction(field) {
      this.$emit("onFieldAction", field);
    },
    addItem(item, rowName, fieldName) {
      log.log("addItem", item, rowName, fieldName);
      this.sortedRows
        .filter((row) => row.name === rowName)[0]
        .fields.filter((field) => field.name === fieldName)[0]
        .value.push(item);
    },
    removeItem(item, rowName, fieldName) {
      this.sortedRows
        .filter((row) => row.name === rowName)[0]
        .fields.filter((field) => field.name === fieldName)[0]
        .value.splice(
          this.sortedRows
            .filter((row) => row.name === rowName)[0]
            .fields.filter((field) => field.name === fieldName)[0]
            .value.indexOf(item),
          1
        );
    },
    saveForm() {
      var isValid = this.checkForm();
      if (isValid) {
          var result = {};
          for (const field of this.allFields) {
              if(field.value !== null && field.value !== undefined && field.type !== "button" && field.type !== "slot" && field.type !== "section"){
                  result[field.name] = field.value;
              }
          }

        this.$emit("onSubmit", result);
      }
    },
    checkForm() {
      var isValid = true;
      var notValidFieldsCount = 0;

      for (var i in this.allFields) {
        if (
          this.allFields[i].type === "text" ||
          this.allFields[i].type === "textarea" ||
          this.allFields[i].type === "file"
        ) {
          if (
            this.allFields[i].value === null ||
            this.allFields[i].value === undefined
          ) {
            this.allFields[i].value = "";
          }

          if (
            this.allFields[i].required === true &&
            this.allFields[i].value.length === 0
          ) {
            log.log("not valid", this.allFields[i].value);
            this.allFields[i].inputClass = this.allFields[i].invalidInputClass;
            this.allFields[i].inputFeedbackText = this.defaultInputFeedbackText;
            notValidFieldsCount++;
          } else if (
              this.allFields[i].required === true &&
            this.allFields[i].value.length < this.allFields[i].minLength
          ) {
            this.allFields[i].inputClass = this.defaultInvalidInputClass;
            this.allFields[i].inputFeedbackText =
              "Pole musi zawierać co najmniej " +
              this.allFields[i].minLength +
              " znaków";
            notValidFieldsCount++;
          } else if (
              this.allFields[i].required === true &&
            this.allFields[i].value.length > this.allFields[i].maxLength
          ) {
            this.allFields[i].inputClass = this.defaultInvalidInputClass;
            this.allFields[i].inputFeedbackText =
              "Pole może zawierać maksymalnie " +
              this.allFields[i].maxLength +
              " znaków";
            notValidFieldsCount++;
          } else if (
            this.allFields[i].pattern !== null &&
            this.allFields[i].pattern !== undefined &&
            this.allFields[i].pattern.length > 0
          ) {
            var pattern = new RegExp(this.allFields[i].pattern);
            if (this.allFields[i].value.length > 0 && !pattern.test(this.allFields[i].value)) {
              this.allFields[i].inputClass = this.defaultInvalidInputClass;
              this.allFields[i].inputFeedbackText =
                "Pole zawiera niedozwolone znaki";
              notValidFieldsCount++;
            } else {
              this.allFields[i].inputClass = this.defaultValidInputClass;
            }
          } else {
            this.allFields[i].inputClass = this.defaultValidInputClass;
          }
        }

        if (this.allFields[i].type === "number") {
          log.log("Number field:", this.allFields[i].name);
          log.log("Number value:", this.allFields[i].value);
          if (
            this.allFields[i].value === null ||
            this.allFields[i].value === undefined
              || this.allFields[i].value === ""
          ) {
            this.allFields[i].value = 0;
          }

          if (
            this.allFields[i].required === true &&
            this.allFields[i].value.length === 0
          ) {
            this.allFields[i].inputClass = this.defaultInvalidInputClass;
            this.allFields[i].inputFeedbackText = this.defaultInputFeedbackText;
            notValidFieldsCount++;
          } else if (this.allFields[i].value < this.allFields[i].minValue) {
            this.allFields[i].inputClass = this.defaultInvalidInputClass;
            this.allFields[i].inputFeedbackText =
              "Pole musi liczbę o minimalnej wartości " +
              this.allFields[i].minValue;
            notValidFieldsCount++;
          } else if (this.allFields[i].value > this.allFields[i].maxValue) {
            this.allFields[i].inputClass = this.defaultInvalidInputClass;
            this.allFields[i].inputFeedbackText =
              "Pole musi liczbę o maksymalnej wartości " +
              this.allFields[i].maxValue;
          } else {
            var integerPattern = new RegExp("\\d+");
            var doublePattern = new RegExp("\\d*[.]\\d+");

            if (
              integerPattern.test(this.allFields[i].value) ||
              doublePattern.test(this.allFields[i].value)
            ) {
              log.log("Pattern not valid", this.allFields[i].value);
              this.allFields[i].inputClass = this.defaultValidInputClass;
            } else {
              this.allFields[i].inputClass = this.defaultInvalidInputClass;
              this.allFields[i].inputFeedbackText =
                this.defaultInputFeedbackText;
              notValidFieldsCount++;
            }
          }
        }

        if (this.allFields[i].type === "select") {
          if (
            this.allFields[i].required === true &&
            (this.allFields[i].value === null ||
              this.allFields[i].value === undefined ||
              this.allFields[i].value.length === 0)
          ) {
            this.allFields[i].inputClass = this.defaultInvalidInputClass;
            this.allFields[i].inputFeedbackText =
              "Wybierz jedną z dostępnych opcji";
            notValidFieldsCount++;
          } else {
            this.allFields[i].inputClass = this.defaultValidInputClass;
          }
        }

        if (this.allFields[i].type === "date") {
          if (
            this.allFields[i].required === true &&
            (this.allFields[i].value === null ||
              this.allFields[i].value === undefined ||
              this.allFields[i].value.length === 0)
          ) {
            this.allFields[i].inputClass = this.defaultInvalidInputClass;
            this.allFields[i].inputFeedbackText =
              "Pole musi zawierać datę w formacie dd.mm.rrrr";
            notValidFieldsCount++;
          } else {
            this.allFields[i].inputClass = this.defaultValidInputClass;
          }
        }

        if (this.allFields[i].type === "checkbox") {
          if (
            this.allFields[i].required === true &&
            (this.allFields[i].value === null ||
              this.allFields[i].value === undefined ||
              this.allFields[i].value === false)
          ) {
            this.allFields[i].inputClass = this.allFields[i].invalidInputClass;
            this.allFields[i].inputFeedbackText = "Opcja musi być zaznaczona";
            notValidFieldsCount++;
          } else {
            this.allFields[i].inputClass = "form-checkbox-valid-feedback";
          }
        }

        if (this.allFields[i].type === "radio") {
          if (
            this.allFields[i].required === true &&
            (this.allFields[i].value === null ||
              this.allFields[i].value === undefined)
          ) {
            this.allFields[i].inputClass = this.allFields[i].invalidInputClass;
            this.allFields[i].inputFeedbackText =
              "Wybierz conajmniej jedną opcję";
            notValidFieldsCount++;
          } else {
            this.allFields[i].inputClass = "form-radio-valid-feedback";
          }
        }
      }

      if (notValidFieldsCount > 0) {
        isValid = false;
      } else {
        isValid = true;
      }

      return isValid;
    },
  },
  watch: {
  },
  created() {
    this.sortedRows = JSON.parse(JSON.stringify(this.structure));
    // Fulfilling fields with data
    if(this.data != null){
        for (const row of this.sortedRows) {
            for(const field of row.fields){
              var fieldValue = this.data[field.name]
                if(fieldValue === null || fieldValue === undefined || fieldValue === "null"){
                  field.value = "";

                }else{
                  field.value = this.data[field.name];
                }
            }
        }
    }


    this.allFields = this.sortedRows
      .map((row) => row.fields)
      .reduce((a, b) => a.concat(b), []);

    for (const i in this.allFields) {
      if (this.allFields[i].type === "checkbox") {
        this.allFields[i].inputClass = "form-checkbox-valid-feedback";
        this.allFields[i].invalidInputClass = "form-checkbox-invalid-feedback";
      } else if (this.allFields[i].type === "radio") {
        this.allFields[i].inputClass = "form-radio-valid-feedback";
        this.allFields[i].invalidInputClass = "form-radio-invalid-feedback";
      } else if(this.allFields[i].type === "button"){
        // do not override inputClass
      }else {
        this.allFields[i].inputClass = this.defaultValidInputClass;
        this.allFields[i].inputFeedbackText = this.defaultInputFeedbackText;
        this.allFields[i].invalidInputClass = this.defaultInvalidInputClass;
      }
    }
  },
};
</script>

<style scoped>
.common-form {
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 0.9rem;
  color: #212529;
}

/* Title */
.common-form__title {
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1.25rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
}

/* Field wrapper */
.common-form__field {
  display: flex;
  flex-direction: column;
}

/* Label */
.common-form__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.3rem;
  letter-spacing: 0.01em;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Required asterisk */
.common-form__required {
  color: #dc3545;
  font-size: 0.85em;
  line-height: 1;
}

/* Help icon */
.common-form__help-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background-color: #adb5bd;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  cursor: help;
  flex-shrink: 0;
}

.common-form__help-icon:hover {
  background-color: #6c757d;
}

/* Inputs */
.common-form__input {
  border-radius: 6px;
  border: 1px solid #ced4da;
  padding: 0.4rem 0.65rem;
  font-size: 0.875rem;
  color: #212529;
  background-color: #fff;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  width: 100%;
}

.common-form__input:focus {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
}

.common-form__input:disabled {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
  opacity: 0.85;
}

.common-form__input.is-invalid {
  border-color: #dc3545;
}

.common-form__input.is-invalid:focus {
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.15);
}

/* Select specific */
select.common-form__input {
  appearance: auto;
}

/* Textarea specific */
textarea.common-form__input {
  resize: vertical;
  min-height: 80px;
}

/* List container */
.common-form__list-container {
  border: 1px solid #ced4da;
  border-radius: 6px;
  height: 200px;
  overflow-y: auto;
  background-color: #fff;
}

.common-form__list-container.is-invalid {
  border-color: #dc3545;
}

/* Section */
.common-form__section {
  margin-top: 0.5rem;
  margin-bottom: 0.75rem;
}

.common-form__section-description {
  font-size: 0.78rem;
  color: #6c757d;
  margin-top: 0.25rem;
  padding-bottom: 0.25rem;
}

/* Radio group */
.common-form__radio-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: #fff;
}

/* Checkbox / radio feedback */
.form-checkbox-invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.8em;
  color: #dc3545;
}

.form-checkbox-valid-feedback {
  display: none;
}

.form-radio-invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.8em;
  color: #dc3545;
}

.form-radio-valid-feedback {
  display: none;
}

/* Footer */
.common-form__footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.6rem;
  padding-top: 1.25rem;
  margin-top: 0.5rem;
  border-top: 1px solid #e9ecef;
}

.common-form__footer-btn {
  min-width: 100px;
  padding: 0.4rem 1.2rem;
  font-size: 0.875rem;
  border-radius: 6px;
}

@media (max-width: 576px) {
  .common-form__footer {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .common-form__footer-btn {
    width: 100%;
    text-align: center;
  }
}
</style>
